import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useHead, useRoute, useRuntimeConfig, useSeoMeta } from "#imports";
import { faqSchemaAnswerKeys } from "~/utils/faqSchemaContent.js";
import { createOrganizationSchema, createWebSiteSchema } from "~/utils/schemas.js";

const plainText = (value) =>
  value
    .replace(/\[br\]/gi, " ")
    .replace(/\[(?:\/)?(?:strong|link|nuxtlink)(?:\s[^\]]*)?\]/gi, "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const useFaqSeoMeta = (questionKey, answerKey) => {
  const { locale, t } = useI18n();
  const route = useRoute();
  const baseUrl = useRuntimeConfig().public.siteUrl;
  const title = computed(() => `${t(questionKey)} | Tradeview Europe`);
  const description = computed(() =>
    (Array.isArray(answerKey) ? answerKey : [answerKey])
      .map((key) => plainText(t(key)))
      .join(" ")
  );
  const answerText = computed(() =>
    (faqSchemaAnswerKeys[questionKey] || (Array.isArray(answerKey) ? answerKey : [answerKey]))
      .map((key) => plainText(t(key)))
      .filter(Boolean)
      .join(" ")
  );

  useSeoMeta({
    title,
    description,
    ogTitle: title,
    ogDescription: description,
    twitterTitle: title,
    twitterDescription: description,
  });

  useHead(() => {
    const path = route.path.endsWith("/") ? route.path : `${route.path}/`;
    const url = `${baseUrl}${path}`;
    const question = t(questionKey);
    const schema = {
      "@context": "https://schema.org",
      "@graph": [
        createOrganizationSchema(t, baseUrl),
        createWebSiteSchema(baseUrl),
        {
          "@type": ["WebPage", "FAQPage"],
          "@id": `${url}#webpage`,
          url,
          name: question,
          inLanguage: locale.value,
          isPartOf: { "@id": `${baseUrl}/#website` },
          publisher: { "@id": `${baseUrl}/#organization` },
          mainEntity: {
            "@type": "Question",
            name: question,
            acceptedAnswer: {
              "@type": "Answer",
              text: answerText.value,
            },
          },
        },
      ],
    };

    return {
      script: [{
        key: "faq-structured-data",
        type: "application/ld+json",
        innerHTML: JSON.stringify(schema).replace(/</g, "\\u003c"),
      }],
    };
  });
};
